/**
 * Learner space → Help and complaints (D-129): the published procedure, a new request (topic,
 * formal complaint, invoice number, files), the learner's requests with their status, the
 * answers of the team, a reply with a file and the satisfaction survey once solved.
 */

import "../pages/support/support.css";

import {
  type SupportTicketCategoryEnum,
  useSupportMeOptionsRetrieve,
  useSupportMeTicketsCreate,
  useSupportMeTicketsCsatCreate,
  useSupportMeTicketsList,
  useSupportMeTicketsReplyCreate,
  useSupportMeTicketsRetrieve,
} from "@agencyos/api-client";
import { canReply, checkFile, statusTone } from "@agencyos/core";
import { Badge, type BadgeTone, Button, Checkbox, GlassCard, SelectField, Skeleton, TextAreaField, TextField } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { type ChangeEvent, type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { fieldErrors } from "../lib/settings";
import { dateTimeText } from "../pages/billing/shared";
import { RatingForm } from "../pages/support/Rating";
import { PageHead } from "./ui";

function useRefresh() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0] ?? "").startsWith("/api/v1/support/me/") });
}

function useFiles(rules: { max_bytes?: number; types?: string[] } | undefined) {
  const { t } = useTranslation();
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState("");
  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const chosen = Array.from(event.target.files ?? []);
    const bad = chosen.find((file) => checkFile(file, rules ?? {}) !== "ok");
    if (bad) {
      setError(t(checkFile(bad, rules ?? {}) === "type" ? "support.ticket.fileType" : "support.ticket.fileSize", { name: bad.name }));
      setFiles([]);
      return;
    }
    setError("");
    setFiles(chosen);
  };
  return { files, error, onChange, reset: () => setFiles([]) };
}

function NewRequest() {
  const { t } = useTranslation();
  const refresh = useRefresh();
  const options = useSupportMeOptionsRetrieve();
  const data = options.data?.status === 200 ? options.data.data : undefined;
  const create = useSupportMeTicketsCreate({ mutation: { onSuccess: refresh } });
  const [form, setForm] = useState({ subject: "", body: "", category: "access", complaint: false, invoice: "" });
  const files = useFiles(data?.attachments as { max_bytes?: number; types?: string[] } | undefined);
  const errors = fieldErrors(create.error);
  const [sent, setSent] = useState("");
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate(
      {
        data: {
          subject: form.subject.trim(),
          body: form.body.trim(),
          category: form.category as SupportTicketCategoryEnum,
          complaint: form.complaint,
          invoice_number: form.invoice.trim(),
          files: files.files,
        },
      },
      {
        onSuccess: (response) => {
          if (response.status !== 201) return;
          setSent(response.data.number);
          setForm({ subject: "", body: "", category: "access", complaint: false, invoice: "" });
          files.reset();
        },
      },
    );
  };
  return (
    <GlassCard as="section" className="stack" aria-labelledby="help-new-title">
      <h2 id="help-new-title">{t("support.me.new")}</h2>
      {data?.procedure.length ? (
        <details>
          <summary>{t("support.me.procedure")}</summary>
          <ol>
            {data.procedure.map((line, index) => (
              <li key={index}>{line}</li>
            ))}
          </ol>
        </details>
      ) : null}
      <form className="stack" onSubmit={onSubmit} noValidate aria-label={t("support.me.new")}>
        <SelectField
          label={t("support.me.category")}
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
          options={(data?.categories ?? []).map((row) => {
            const item = row as { code?: string; label?: string };
            return { value: item.code ?? "", label: item.label ?? "" };
          })}
        />
        <TextField label={t("support.me.subject")} value={form.subject} maxLength={200} error={errors.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
        <TextAreaField label={t("support.me.body")} value={form.body} rows={5} error={errors.body ?? errors.detail} onChange={(e) => setForm({ ...form, body: e.target.value })} />
        <Checkbox
          label={t("support.me.complaint")}
          hint={t("support.me.complaintHint", { days: data?.complaint_days ?? 5 })}
          checked={form.complaint}
          onChange={(e) => setForm({ ...form, complaint: e.target.checked })}
        />
        {form.category === "billing" || form.category === "refund" ? (
          <TextField label={t("support.me.invoice")} value={form.invoice} onChange={(e) => setForm({ ...form, invoice: e.target.value })} />
        ) : null}
        <label className="ao-field">
          <span className="ao-field__label">{t("support.me.files")}</span>
          <input type="file" multiple onChange={files.onChange} accept={((data?.attachments as { types?: string[] } | undefined)?.types ?? []).join(",")} />
          <small className="ao-field__hint">{t("support.ticket.filesHint")}</small>
          {files.error || errors.files ? (
            <small role="alert" className="ao-field__error">
              {files.error || errors.files}
            </small>
          ) : null}
        </label>
        <Button type="submit" disabled={create.isPending || !form.subject.trim() || !form.body.trim() || Boolean(files.error)}>
          {create.isPending ? t("common.loading") : t("support.me.send")}
        </Button>
        {sent ? <p role="status">{t("support.me.sent", { number: sent })}</p> : null}
      </form>
    </GlassCard>
  );
}

export function HelpPage() {
  const { t, i18n } = useTranslation();
  const list = useSupportMeTicketsList({ query: { retry: false } });
  const rows = list.data?.status === 200 ? list.data.data : undefined;
  if (list.isError && !rows) {
    return (
      <div className="ac-page stack">
        <PageHead title={t("support.me.title")} />
        <p role="alert">{t("support.me.unavailable")}</p>
      </div>
    );
  }
  return (
    <div className="ac-page stack">
      <PageHead title={t("support.me.title")} subtitle={t("support.me.intro")} />
      <NewRequest />
      <GlassCard as="section" className="stack" aria-labelledby="help-mine-title">
        <h2 id="help-mine-title">{t("support.me.mine")}</h2>
        {list.isPending ? <Skeleton style={{ height: 80 }} /> : null}
        {rows && !rows.length ? <p className="muted">{t("support.me.empty")}</p> : null}
        <ul className="plain-list stack">
          {(rows ?? []).map((row) => (
            <li key={row.id} className="row between">
              <span>
                <Link to={`/academy/support/${row.id}`}>
                  {row.number} · {row.subject}
                </Link>
                <br />
                <small className="muted">{dateTimeText(row.created_at, i18n.language)}</small>
              </span>
              <Badge tone={statusTone(row.status) as BadgeTone}>{t(`support.status.${row.status}`)}</Badge>
            </li>
          ))}
        </ul>
      </GlassCard>
    </div>
  );
}

export function HelpTicketPage() {
  const { t, i18n } = useTranslation();
  const { id = "" } = useParams();
  const refresh = useRefresh();
  const query = useSupportMeTicketsRetrieve(id);
  const options = useSupportMeOptionsRetrieve();
  const rules = options.data?.status === 200 ? (options.data.data.attachments as { max_bytes?: number; types?: string[] }) : undefined;
  const reply = useSupportMeTicketsReplyCreate({ mutation: { onSuccess: refresh } });
  const rate = useSupportMeTicketsCsatCreate({ mutation: { onSuccess: refresh } });
  const [body, setBody] = useState("");
  const files = useFiles(rules);
  const ticket = query.data?.status === 200 ? query.data.data : undefined;
  if (!ticket) return <div className="ac-page">{query.isPending ? <Skeleton style={{ height: 200 }} /> : <p role="alert">{t("common.error")}</p>}</div>;
  return (
    <div className="ac-page stack">
      <Link to="/academy/support">{t("support.me.back")}</Link>
      <PageHead
        title={`${ticket.number} · ${ticket.subject}`}
        subtitle={
          ticket.is_complaint && ticket.resolution_due_at
            ? t("support.me.complaintDue", { date: dateTimeText(ticket.resolution_due_at, i18n.language) })
            : ticket.first_response_due_at && ticket.status === "new"
              ? t("support.me.firstAnswer", { date: dateTimeText(ticket.first_response_due_at, i18n.language) })
              : undefined
        }
        aside={<Badge tone={statusTone(ticket.status) as BadgeTone}>{t(`support.status.${ticket.status}`)}</Badge>}
      />
      <GlassCard as="section" className="stack" aria-label={t("support.ticket.thread")}>
        <ol className="plain-list support-thread">
          {ticket.messages.map((message) => (
            <li key={message.id} className={`bubble ${message.from_customer ? "bubble--out" : "bubble--in"}`}>
              <p className="bubble__meta">
                {message.from_customer ? t("support.me.you") : message.author_name || t("support.me.agency")} · {dateTimeText(message.created_at, i18n.language)}
              </p>
              <p className="bubble__body support-body">{message.body}</p>
              {message.attachments.map((file) => (
                <p key={file.id}>
                  {file.url ? (
                    <a href={file.url} target="_blank" rel="noreferrer">
                      {file.name}
                    </a>
                  ) : (
                    file.name
                  )}
                </p>
              ))}
            </li>
          ))}
        </ol>
      </GlassCard>
      {ticket.can_rate ? (
        <GlassCard as="section" className="stack">
          <RatingForm pending={rate.isPending} onSubmit={(value) => rate.mutate({ id: ticket.id, data: value })} />
        </GlassCard>
      ) : null}
      {rate.isSuccess ? <p role="status">{t("support.me.thanks")}</p> : null}
      {canReply(ticket.status) ? (
        <GlassCard as="section" className="stack">
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              reply.mutate(
                { id: ticket.id, data: { body: body.trim(), files: files.files } },
                {
                  onSuccess: () => {
                    setBody("");
                    files.reset();
                  },
                },
              );
            }}
            aria-label={t("support.me.reply")}
          >
            <TextAreaField label={t("support.me.reply")} value={body} rows={4} onChange={(e) => setBody(e.target.value)} />
            <label className="ao-field">
              <span className="ao-field__label">{t("support.me.files")}</span>
              <input type="file" multiple onChange={files.onChange} accept={(rules?.types ?? []).join(",")} />
              {files.error ? (
                <small role="alert" className="ao-field__error">
                  {files.error}
                </small>
              ) : null}
            </label>
            <Button type="submit" disabled={reply.isPending || !body.trim() || Boolean(files.error)}>
              {t("support.me.send")}
            </Button>
            {reply.isSuccess && !body ? <span role="status">{t("support.me.replySent")}</span> : null}
          </form>
        </GlassCard>
      ) : (
        <p className="muted">{t("support.me.closed")}</p>
      )}
    </div>
  );
}
